import React, { CSSProperties, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripDotsVertical } from '@fortawesome/pro-solid-svg-icons';
import { WORKLIST_MAPPING_ANY_MATCH, WorklistScriptMapping } from '../../reducers/worklistReducer';
import { faPlusCircle, faTrash, faWrench } from '@fortawesome/free-solid-svg-icons';
import WorklistScriptsMappingForm from './WorklistScriptsMappingForm';
import { useAppDispatch } from '../../hooks';
import * as worklistActions from '../../actions/worklistActions';
import useToaster from '../../actions/useToaster';
import { arrayMove, SortableContext, useSortable } from '@dnd-kit/sortable';
import { Active, closestCenter, DndContext, Over } from '@dnd-kit/core';
import { Link } from 'react-router-dom';
import { availableOperations, User } from '../../reducers/userReducer';
import { canPerformAction } from '../../actions/userActions';

export default function WorklistScriptsMappings({ user }: { user: User }) {
  const dispatch = useAppDispatch();
  const toaster = useToaster();

  const authorized = useMemo(() => canPerformAction(user, availableOperations.CAN_MANAGE_NODES), [user]);
  const scriptsAuthorized = useMemo(() => canPerformAction(user, availableOperations.SCRIPT_CATALOG_VISIBLE), [user]);

  const [shownData, setShownData] = useState<WorklistScriptMapping[]>([]);
  const [showNewMapping, setShowNewMapping] = useState<boolean>(false);
  const [updatingMapping, setUpdatingMapping] = useState<WorklistScriptMapping | null>(null);
  const [deletingMapping, setDeletingMapping] = useState<WorklistScriptMapping | null>(null);

  useEffect(() => {
    dispatch(worklistActions.getWorklistScriptsMappings()).then(setShownData);
  }, []);

  const onDragEnd = ({ active, over }: { active: Active; over: Over | null }) => {
    if (over && active.id !== over.id) {
      const oldIndex = shownData.findIndex(item => item.pk === active.id);
      const newIndex = shownData.findIndex(item => item.pk === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedData = arrayMove([...shownData], oldIndex, newIndex);
        setShownData(reorderedData);

        const start = Math.min(oldIndex, newIndex);
        const end = Math.max(oldIndex, newIndex);

        const changedEntries = reorderedData.slice(start, end + 1).map((item, index) => ({
          pk: item.pk,
          orderIndex: start + index,
        }));

        worklistActions
          .updateWorklistScriptMappingOrders(changedEntries)
          .catch(err => {
            console.error('error updating worklist script mapping order', err);
            toaster.error('Error updating worklist script mapping order');
          })
          .finally(() => dispatch(worklistActions.forceGetWorklistScriptsMappings()).then(setShownData));
      }
    }
  };

  return (
    <div id="worklist-scripts-mappings-table">
      <h5>Scripts mappings</h5>
      <div className="worklist-table-header">
        <span>The following mappings will be applied in this order to worklist queries results:</span>
        <Button
          disabled={!authorized}
          size="sm"
          style={{ marginLeft: '2em' }}
          variant="primary"
          onClick={() => setShowNewMapping(true)}
        >
          Add new mapping <FontAwesomeIcon icon={faPlusCircle} style={{ marginLeft: '5px' }} />
        </Button>
      </div>

      <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext disabled={!authorized} items={shownData.map(row => row.pk)}>
          <table id="scripts-mappings" className="queue-table">
            <thead>
              <tr>
                <th style={{ width: '5%' }} />
                <th style={{ width: '25%' }} />
                <th style={{ width: '25%' }} />
                <th style={{ width: '20%' }} />
                <th style={{ width: '10%' }} />
                <th style={{ width: '10%' }} />
              </tr>
            </thead>
            <tbody>
              {shownData.map(row => (
                <Row
                  key={row.pk}
                  mapping={row}
                  authorized={authorized}
                  scriptsAuthorized={scriptsAuthorized}
                  setUpdatingMapping={setUpdatingMapping}
                  setDeletingMapping={setDeletingMapping}
                />
              ))}
            </tbody>
          </table>
        </SortableContext>
      </DndContext>

      {showNewMapping && (
        <AddNewMapping
          onClose={() => setShowNewMapping(false)}
          onSave={mapping => {
            worklistActions
              .insertWorklistScriptMapping(mapping)
              .then(() => {
                dispatch(worklistActions.forceGetWorklistScriptsMappings()).then(setShownData);
                toaster.success('Worklist script mapping added successfully');
              })
              .catch(err => {
                console.error('error adding worklist script mapping', err);
                toaster.error('Error adding worklist script mapping');
              })
              .finally(() => setShowNewMapping(false));
          }}
        />
      )}
      {updatingMapping && (
        <UpdateMapping
          mapping={updatingMapping}
          onClose={() => setUpdatingMapping(null)}
          onSave={mapping => {
            worklistActions
              .updateWorklistScriptMapping(mapping)
              .then(() => {
                dispatch(worklistActions.forceGetWorklistScriptsMappings()).then(setShownData);
                toaster.success('Worklist script mapping updated successfully');
              })
              .catch(err => {
                console.error('error updating worklist script mapping', err);
                toaster.error('Error updating worklist script mapping');
              })
              .finally(() => setUpdatingMapping(null));
          }}
        />
      )}
      {deletingMapping && (
        <DeleteMapping
          mapping={deletingMapping}
          onClose={() => setDeletingMapping(null)}
          onDelete={() => {
            worklistActions
              .deleteWorklistScriptMapping(deletingMapping.pk)
              .then(() => {
                dispatch(worklistActions.forceGetWorklistScriptsMappings()).then(setShownData);
                toaster.success('Worklist script mapping deleted successfully');
              })
              .catch(err => {
                console.error('error deleting worklist script mapping', err);
                toaster.error('Error deleting worklist script mapping');
              })
              .finally(() => setDeletingMapping(null));
          }}
        />
      )}
    </div>
  );
}

interface IWorklistScriptsMappingRow {
  mapping: WorklistScriptMapping;
  authorized: boolean;
  scriptsAuthorized: boolean;
  setUpdatingMapping: (_: WorklistScriptMapping) => void;
  setDeletingMapping: (_: WorklistScriptMapping) => void;
}

const Row = ({
  mapping,
  authorized,
  scriptsAuthorized,
  setUpdatingMapping,
  setDeletingMapping,
}: IWorklistScriptsMappingRow) => {
  const { setNodeRef, transform, transition, isDragging, attributes, listeners } = useSortable({
    id: mapping.pk,
  });

  const cursorStyle = useMemo(() => {
    return !authorized ? 'default' : isDragging ? 'grabbing' : 'grab';
  }, [authorized, isDragging]);

  const style: CSSProperties = {
    position: 'relative',
    zIndex: isDragging ? 2 : 1,
    cursor: isDragging ? 'grabbing' : 'default',
    transform: transform ? `translateY(${transform.y}px)` : undefined,
    transition,
  };

  return (
    <tr ref={setNodeRef} style={style} className="node-row">
      <td className="wsm-cell">
        <div {...attributes} {...listeners} style={{ cursor: cursorStyle }} title="Drag to reorder">
          <FontAwesomeIcon style={{ cursor: cursorStyle }} icon={faGripDotsVertical} />
        </div>
      </td>
      <td className="wsm-cell">
        {mapping.callingAet === WORKLIST_MAPPING_ANY_MATCH ? 'All calling AETs' : mapping.callingAet}
      </td>
      <td className="wsm-cell">
        {mapping.worklistNode === WORKLIST_MAPPING_ANY_MATCH ? 'All worklist nodes' : mapping.worklistNode}
      </td>
      <td>
        {scriptsAuthorized ? (
          <Button variant="link" size="sm" as={Link} to={`/scripts?name=${mapping.scriptName}`}>
            {mapping.scriptName}
          </Button>
        ) : (
          <React.Fragment>{mapping.scriptName}</React.Fragment>
        )}
      </td>
      <td className="wsm-cell">
        {mapping.enabled ? <Badge variant="success">Enabled</Badge> : <Badge variant="danger">Disabled</Badge>}
      </td>
      <td>
        <div style={{ width: '100%', textAlign: 'right', overflow: 'visible' }}>
          {authorized && (
            <React.Fragment>
              <FontAwesomeIcon icon={faWrench} className="clickable-icon" onClick={() => setUpdatingMapping(mapping)} />
              <FontAwesomeIcon icon={faTrash} className="clickable-icon" onClick={() => setDeletingMapping(mapping)} />
            </React.Fragment>
          )}
        </div>
      </td>
    </tr>
  );
};

interface INewWorklistScriptPopup {
  onClose: () => void;
  onSave: (mapping: WorklistScriptMapping) => void;
}

const AddNewMapping = ({ onClose, onSave }: INewWorklistScriptPopup) => (
  <Modal show size="lg">
    <Modal.Header>
      <Modal.Title>New Worklist scripts mapping</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <WorklistScriptsMappingForm mapping={null} onClose={onClose} onSave={onSave} saveButtonLabel="Add" />
    </Modal.Body>
  </Modal>
);

interface IWorklistUpdatePopup extends INewWorklistScriptPopup {
  mapping: WorklistScriptMapping;
}

const UpdateMapping = ({ mapping, onClose, onSave }: IWorklistUpdatePopup) => (
  <Modal show size="lg">
    <Modal.Header>
      <Modal.Title>
        Edit script mapping from{' '}
        <strong>{mapping.callingAet === WORKLIST_MAPPING_ANY_MATCH ? 'all calling AETs' : mapping.callingAet}</strong> to{' '}
        <strong>{mapping.worklistNode === WORKLIST_MAPPING_ANY_MATCH ? 'all worklist nodes' : mapping.worklistNode}</strong>
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <WorklistScriptsMappingForm mapping={mapping} onClose={onClose} onSave={onSave} saveButtonLabel="Save changes" />
    </Modal.Body>
  </Modal>
);

interface IWorklistDeletePopup {
  mapping: WorklistScriptMapping;
  onClose: () => void;
  onDelete: () => void;
}

const DeleteMapping = ({ mapping, onClose, onDelete }: IWorklistDeletePopup) => {
  return (
    <Modal show>
      <Modal.Header>
        <Modal.Title>Are you sure?</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          You are going to delete the script mapping from{' '}
          <strong>{mapping.callingAet === WORKLIST_MAPPING_ANY_MATCH ? 'all calling AETs' : mapping.callingAet}</strong> to{' '}
          <strong>
            {mapping.worklistNode === WORKLIST_MAPPING_ANY_MATCH ? 'all worklist nodes' : mapping.worklistNode}
          </strong>
          .
        </p>
        <p>Are you sure?</p>
        <div style={{ textAlign: 'right', marginTop: '1em' }}>
          <br />
          <Button variant="outline-secondary" onClick={onClose} style={{ marginRight: '5px' }}>
            Cancel
          </Button>
          <Button variant="danger" onClick={onDelete}>
            Confirm delete
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};
